import React, { useEffect } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues$key } from '@atlassian/jira-relay/src/__generated__/exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues.graphql';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';
import messages from '../../../../common/messages.tsx';
import type { AsyncExportItem, ExportIssuesSubscriptionError } from '../../../../common/types.tsx';
import {
	ExportErrorFlag,
	type ExportErrorFlagProps,
} from '../../../../common/ui/export-error-flag/index.tsx';
import { getStatusCodeGroup, is4xxError } from '../../../../common/utils.tsx';

type Props = {
	exportIssuesTask: exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues$key | null;
	exportItem: AsyncExportItem;
	error?: ExportIssuesSubscriptionError;
	onDismissed: () => void;
};

/**
 * Flag to display when an export issues task has errored.
 * there are 2 sources of error:
 * 1. error coming from onError callback of the subscription
 * 2. JiraIssueExportTaskTerminated error coming from the exportIssuesTask itself
 */
export const ExportIssuesTaskErrorFlag = ({
	exportIssuesTask,
	exportItem,
	error: errorProp,
	onDismissed,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const exportIssuesTaskData =
		useFragment<exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment exportIssuesTaskErrorFlag_issueNavigatorActionExportIssues on JiraIssueExportEvent {
					... on JiraIssueExportTaskTerminated {
						taskProgress {
							task {
								id
							}
						}
						error {
							displayMessage
							errorType
							statusCode
						}
					}
				}
			`,
			exportIssuesTask,
		);

	const statusCode = errorProp?.extensions?.statusCode || exportIssuesTaskData?.error?.statusCode;
	const errorType = errorProp?.extensions?.errorType || exportIssuesTaskData?.error?.errorType;

	let errorTitle;
	let errorMessage;
	let actions: ExportErrorFlagProps['actions'];

	useEffect(() => {
		fireErrorAnalytics({
			event: createAnalyticsEvent({}), // forces contextual attributes be included
			meta: {
				id: 'JiraIssueExportTaskExportFailed',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			sendToPrivacyUnsafeSplunk: true,
			error: errorProp || new Error(exportIssuesTaskData?.error?.displayMessage || undefined),
			attributes: {
				statusCodeGroup: getStatusCodeGroup(statusCode),
				classification: errorProp?.extensions?.classification,
				errorSource: errorProp?.extensions?.errorSource,
				taskId: exportIssuesTaskData?.taskProgress?.task?.id,
				errorType,
				statusCode,
				isSubscriptionFailure: Boolean(errorProp),
			},
		});

		// We only want to send error analytics on mount
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (is4xxError(statusCode)) {
		switch (statusCode) {
			case 429:
				errorTitle = formatMessage(messages.exportIssuesAlreadyInProgressTitle, {
					exportItemLabel: exportItem.label,
				});
				errorMessage = formatMessage(messages.exportIssuesAlreadyInProgressDescription);
				actions = [
					{
						content: formatMessage(messages.exportIssuesAlreadyInProgressCancelExportAction),
						onClick: onDismissed,
					},
				];
				break;
			default:
				errorMessage = errorProp?.message || exportIssuesTaskData?.error?.displayMessage;
				break;
		}
	}

	return (
		<ExportErrorFlag
			id={`export-issues-task-flag-error-${exportItem.id}`}
			errorTitle={errorTitle}
			errorMessage={errorMessage}
			onDismissed={onDismissed}
			actions={actions}
		/>
	);
};
